import { formatFreshnessDateLabel } from './freshness-serp-compare';

/** Mirrors `ContentGapSignalSource` on the backend (§0at). */
export type ContentGapSignalSource = 'opportunity_snapshot' | 'rival_coverage';

/**
 * What the `Sections` chip says on hover.
 *
 * Two stores feed this chip and they answer different questions: the
 * opportunity build compares the page against the whole marked competitor set,
 * the review belt against the ONE best-ranked rival on this page's own SERP.
 * They are also filled by separate builds on separate days — weeks apart on
 * both pilot domains — so a queue that renders both as the same bare number
 * claims an "as of" date it does not have.
 *
 * Hence: name the comparison, and name the day it was measured. No staleness
 * threshold is applied. Picking a number of days past which a section
 * comparison stops counting would be a threshold nobody derived from the
 * corpus, which is the mistake §0aq records; showing the date lets the reader
 * judge it.
 */
export function contentGapSignalTooltip(input: {
  sectionCount: number;
  source?: ContentGapSignalSource;
  measuredAt?: string;
}): string {
  const count = input.sectionCount;
  if (count <= 0) return 'Sections';

  const noun = count === 1 ? 'section' : 'sections';
  const basis =
    input.source === 'rival_coverage'
      ? `${count} ${noun} the top-ranked rival covers and this page does not`
      : `${count} ${noun} missing vs competitors`;

  const measured = formatContentGapMeasuredAt(input.measuredAt);
  return measured ? `${basis} · measured ${measured}` : basis;
}

export function formatContentGapMeasuredAt(measuredAt?: string): string | null {
  if (!measuredAt) return null;
  const parsed = new Date(measuredAt);
  if (Number.isNaN(parsed.getTime())) return null;
  return formatFreshnessDateLabel(parsed) || null;
}
